import type { BtlApplication } from 'api/btlApplicationForm/types';
import { monthsToYears } from 'utils/numbers';
import { resolveMissingFieldsWarnings, resolvePropertyStatusWarnings } from './missingFieldsWarnings';
import type { BuildBtlCaseSidebarParams } from '../types';
import { CaseSidebarItemId } from '../types';

function formatFtv(ftv?: number): string {
  if (ftv === undefined || ftv === null) {
    return '';
  }

  return `FTV: ${(ftv * 100).toFixed(0)}%`;
}

export function resolveItemSubTitle(itemId: CaseSidebarItemId, application: BtlApplication): string {
  switch (itemId) {
    case CaseSidebarItemId.FINANCING: {
      const purpose = application.purpose
        ? application.purpose.charAt(0).toUpperCase() + application.purpose.slice(1).toLowerCase()
        : '';
      const ftv = formatFtv(application.netFinancingFtv);

      return [purpose, ftv].filter(Boolean).join(' / ');
    }
    case CaseSidebarItemId.APPLICANTS:
      return '';
    case CaseSidebarItemId.PRODUCT: {
      const product = application.product;

      if (!product) {
        return '';
      }

      const term = monthsToYears(product.monthsInitial);
      const ftv = (product.maximumFTV * 100).toFixed(0);
      const productType = product.productType?.toLowerCase() ?? '';

      return `${term}yr ${productType} ${ftv}% FTV`;
    }
    default:
      return '';
  }
}

export function resolveItemIsDisabled(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): boolean {
  const { application, isAffordabilityPassed } = context;
  const companiesCount = application.borrower?.companies.length ?? 0;
  const individualsCount = application.borrower?.individuals.length ?? 0;

  switch (itemId) {
    case CaseSidebarItemId.APPLICANTS:
      return !application.id;
    case CaseSidebarItemId.PRODUCT:
      return companiesCount + individualsCount === 0;
    case CaseSidebarItemId.TOP_SLICING:
    case CaseSidebarItemId.AFFORDABILITY_CHECK:
      return !application.product;
    case CaseSidebarItemId.SUBMIT_DIP:
      return !isAffordabilityPassed;
    default:
      return false;
  }
}

export function resolveItemWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  if (resolveItemIsDisabled(itemId, context)) {
    return [];
  }

  const warnings: string[] = [];

  warnings.push(...resolveMissingFieldsWarnings(itemId, context));
  warnings.push(...resolvePropertyStatusWarnings(itemId, context));

  if (itemId === CaseSidebarItemId.AFFORDABILITY_CHECK && context.isAffordabilityRunRequired) {
    warnings.push(context.formatMessage({ id: 'caseSidebar.warnings.affordabilityCheckOutdated' }));
  }

  return warnings;
}
